/**
 * Parcours d'entrée et d'achat (admin) — RPC `admin_funnel_stats` (migration
 * 0701) : visiteurs de chaque page d'entrée (accueil, Découverte, démo,
 * connexion, onboarding) et d'achat (débloquer, page d'achat, abonnement,
 * proche, retour de paiement), avec les événements de la démo, les comptes
 * créés et les ventes. Mêmes exclusions que les statistiques en direct.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { StatsRange } from './live-stats';

export type FunnelGroup = 'entry' | 'buy';

export interface FunnelPage {
  path: string;
  label: string | null;
  visitors: number;
  views: number;
}

export interface FunnelStep {
  group: FunnelGroup;
  key: string;
  label: string;
  visitors: number;
  prev_visitors: number;
  views: number;
  pages: FunnelPage[];
}

export interface CurPrev {
  cur: number;
  prev: number;
}

export interface FunnelStats {
  range: StatsRange;
  from: string;
  steps: FunnelStep[];
  demo: {
    started: number;
    prev_started: number;
    completed: number;
    prev_completed: number;
    signup_clicked: number;
    prev_signup_clicked: number;
  };
  signups: CurPrev;
  sales: CurPrev & { revenue_cents: number; prev_revenue_cents: number };
  subscriptions: CurPrev;
}

/** Étapes d'un groupe, dans l'ordre du parcours. */
export function stepsOf(stats: Pick<FunnelStats, 'steps'>, group: FunnelGroup): FunnelStep[] {
  return stats.steps.filter((s) => s.group === group);
}

/** Visiteurs d'une étape (0 si elle manque). */
export function stepVisitors(steps: readonly FunnelStep[], key: string): number {
  return steps.find((s) => s.key === key)?.visitors ?? 0;
}

const PCT = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/** « 3 % », « 0,5 % », ou null quand la base est vide. */
export function rateLabel(part: number, base: number): string | null {
  if (base <= 0) return null;
  const pct = (part / base) * 100;
  return `${PCT.format(pct >= 10 ? Math.round(pct) : pct)} %`;
}

/** Admin : parcours, rechargé toutes les 60 s. */
export function useFunnelStats(range: StatsRange, enabled: boolean) {
  return useQuery({
    queryKey: ['admin-funnel-stats', range],
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<FunnelStats> => {
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db.rpc('admin_funnel_stats', { p_range: range });
      if (error) throw error;
      return data as FunnelStats;
    },
  });
}
